import React, { useState, useEffect } from 'react';
import {  Box,  Typography,  Paper,  Button,  Table,  TableBody,  TableCell,  TableContainer,  TableHead,  TableRow,  TablePagination,  IconButton,  Chip,  Dialog,  DialogTitle,  DialogContent,  DialogActions,  TextField,  MenuItem,  Select,  FormControl,  InputLabel,  Tooltip,  Alert,  Snackbar,  CircularProgress,  Card,  CardContent,  InputAdornment,  Fade,  Slide,  useMediaQuery,  useTheme,  Stack,  Fab,  Autocomplete,} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Book as BookIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { SubjectService } from '../../services/subjectService';
import { ClassService } from '../../services/classService';
import { SubjectEnrollmentService } from '../../services/subjectEnrollmentService';
import { Subject, SubjectFormData, Class } from '../../types';

interface SubjectStats {
  total: number;
  active: number;
  inactive: number;
  departments: number;
}

export function SubjectManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedSemester, setSelectedSemester] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState<Subject | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [subjectStats, setSubjectStats] = useState<SubjectStats>({ total: 0, active: 0, inactive: 0, departments: 0 });
  const [departments, setDepartments] = useState<string[]>([]);
  const [mounted, setMounted] = useState(false);

  const [formData, setFormData] = useState<SubjectFormData>({
    name: '',
    code: '',
    description: '',
    credits: 3,
    department: '',
    semester: 1,
    classIds: [],
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setMounted(true);
    loadSubjects();
    loadDepartments();
    loadClasses();
  }, [searchTerm, selectedDepartment, selectedSemester]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      let result;
      
      if (searchTerm.trim()) {
        const searchResults = await SubjectService.searchSubjects(searchTerm);
        result = { data: searchResults };
      } else {
        result = await SubjectService.getSubjects(100);
      }

      let filteredSubjects = result.data;
      
      if (selectedDepartment !== 'all') {
        filteredSubjects = filteredSubjects.filter(subject => subject.department === selectedDepartment);
      }
      
      if (selectedSemester !== 'all') {
        filteredSubjects = filteredSubjects.filter(subject => subject.semester === parseInt(selectedSemester));
      }

      setSubjects(filteredSubjects);
      calculateStats(filteredSubjects);
    } catch (error) {
      showSnackbar('Failed to load subjects', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const depts = await SubjectService.getDepartments();
      setDepartments(depts);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadClasses = async () => {
    try {
      const result = await ClassService.getClasses(100);
      setClasses(result.classes);
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  };

  const calculateStats = (allSubjects: Subject[]) => {
    const stats = allSubjects.reduce((acc, subject) => {
      acc.total++;
      if (subject.isActive) acc.active++;
      else acc.inactive++;
      return acc;
    }, { total: 0, active: 0, inactive: 0, departments: 0 });
    
    stats.departments = new Set(allSubjects.map(s => s.department).filter(Boolean)).size;
    setSubjectStats(stats);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Subject name is required';
    }

    if (!formData.code.trim()) {
      errors.code = 'Subject code is required';
    } else if (formData.code.length < 3) {
      errors.code = 'Subject code must be at least 3 characters';
    }

    if (formData.credits < 1 || formData.credits > 10) {
      errors.credits = 'Credits must be between 1 and 10';
    }

    if (formData.semester && (formData.semester < 1 || formData.semester > 8)) {
      errors.semester = 'Semester must be between 1 and 8';
    }

    if (formData.classIds.length === 0) {
      errors.classIds = 'At least one class must be selected';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubject = () => {
    setEditingSubject(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      credits: 3,
      department: '',
      semester: 1,
      classIds: [],
    });
    setValidationErrors({});
    setOpenDialog(true);
  };

  const handleEditSubject = (subject: Subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name,
      code: subject.code,
      description: subject.description || '',
      credits: subject.credits,
      department: subject.department || '',
      semester: subject.semester || 1,
      classIds: subject.classIds || [],
    });
    setValidationErrors({});
    setOpenDialog(true);
  };

  const handleDeleteSubject = (subject: Subject) => {
    setSubjectToDelete(subject);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingSubject) {
        await SubjectService.updateSubject(editingSubject.id, formData);
        showSnackbar('Subject updated successfully', 'success');
      } else {
        await SubjectService.createSubject(formData);
        showSnackbar('Subject created successfully', 'success');
      }
      setOpenDialog(false);
      loadSubjects();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to save subject', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!subjectToDelete) return;

    try {
      await SubjectService.deleteSubject(subjectToDelete.id);
      showSnackbar('Subject deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setSubjectToDelete(null);
      loadSubjects();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to delete subject', 'error');
    }
  };

  const toggleSubjectStatus = async (subject: Subject) => {
    try {
      await SubjectService.toggleSubjectStatus(subject.id);
      showSnackbar(`Subject ${subject.isActive ? 'deactivated' : 'activated'} successfully`, 'success');
      loadSubjects();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to update subject status', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInputChange = (field: keyof SubjectFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'credits' || field === 'semester' ? parseInt(e.target.value) || 0 : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getStatusColor = (isActive: boolean): "default" | "success" | "error" => {
    return isActive ? "success" : "error";
  };

  // Mobile card view for subjects
  const SubjectCard = ({ subject }: { subject: Subject }) => (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
              {subject.code}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {subject.name}
            </Typography>
            {subject.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {subject.description}
              </Typography>
            )}
          </Box>
          <Chip
            label={subject.isActive ? 'Active' : 'Inactive'}
            color={getStatusColor(subject.isActive)}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${subject.credits} Credits`}
            size="small"
            color="info"
            variant="outlined"
          />
          {subject.semester && (
            <Chip
              label={`Sem ${subject.semester}`}
              size="small"
              variant="outlined"
            />
          )}
          {subject.department && (
            <Chip
              label={subject.department}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
        
        {subject.classIds && subject.classIds.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
              Assigned Classes:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {subject.classIds.map((classId) => {
                const classData = classes.find(c => c.id === classId);
                return classData ? (
                  <Chip
                    key={classId}
                    label={`${classData.name} (${classData.students.length} students)`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                ) : null;
              })}
            </Box>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit Subject">
            <IconButton
              size="small"
              onClick={() => handleEditSubject(subject)}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={subject.isActive ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => toggleSubjectStatus(subject)}
              sx={{ color: subject.isActive ? 'warning.main' : 'success.main' }}
            >
              {subject.isActive ? <VisibilityOffIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Subject">
            <IconButton
              size="small"
              onClick={() => handleDeleteSubject(subject)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !mounted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in={mounted} timeout={800}>
      <Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center', 
          mb: 3,
          gap: isMobile ? 2 : 0
        }}>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
            Subject Management
          </Typography>
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSubject}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 8px 24px -8px rgba(25, 118, 210, 0.4)',
              }}
            >
              Add Subject
            </Button>
          )}
        </Box>

        {/* Stats Cards */}
        <Slide direction="up" in={mounted} timeout={1000}>
          <Box sx={{ mb: 4 }}>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={2}
              sx={{ 
                justifyContent: "space-between",
                flexWrap: isMobile ? "nowrap" : "wrap"
              }}
            >
              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <BookIcon color="primary" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          Total Subjects
                        </Typography>
                        <Typography variant="h4" color="primary">
                          {subjectStats.total}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              
              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ViewIcon color="success" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          Active Subjects
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {subjectStats.active}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VisibilityOffIcon color="error" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          Inactive Subjects
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {subjectStats.inactive}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: isMobile ? "none" : "1 1 23%" }}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SchoolIcon color="info" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography color="textSecondary" variant="body2">
                          Departments
                        </Typography>
                        <Typography variant="h4" color="info.main">
                          {subjectStats.departments}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          </Box>
        </Slide>

        {/* Filters */}
        <Slide direction="up" in={mounted} timeout={1200}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)' }}>
            <Stack spacing={2}>
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  size={isMobile ? "medium" : "small"}
                  placeholder="Search subjects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ borderRadius: 2 }}
                />
              </Box>
              
              <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2}
                sx={{ alignItems: isMobile ? "stretch" : "center" }}
              >
                <Box sx={{ flex: isMobile ? "none" : "1" }}>
                  <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                      label="Department"
                    >
                      <MenuItem value="all">All Departments</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ flex: isMobile ? "none" : "1" }}>
                  <FormControl fullWidth size={isMobile ? "medium" : "small"}>
                    <InputLabel>Semester</InputLabel>
                    <Select
                      value={selectedSemester}
                      onChange={(e) => setSelectedSemester(e.target.value)}
                      label="Semester"
                    >
                      <MenuItem value="all">All Semesters</MenuItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                        <MenuItem key={sem} value={sem.toString()}>
                          Semester {sem}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ flex: isMobile ? "none" : "0 0 auto" }}>
                  <Button
                    fullWidth={isMobile}
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadSubjects}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </Paper>
        </Slide>

        {/* Subjects Display - Table for desktop, Cards for mobile */}
        <Slide direction="up" in={mounted} timeout={1400}>
          <Paper sx={{ borderRadius: 3, boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.12)', overflow: 'hidden' }}>
            {isMobile ? (
              // Mobile Card View
              <Box sx={{ p: 2 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : subjects.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No subjects found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {subjects
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((subject) => (
                        <SubjectCard key={subject.id} subject={subject} />
                      ))}
                  </>
                )}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={subjects.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </Box>
            ) : (
              // Desktop Table View
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Subject Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Subject Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Credits</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Semester</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Assigned Classes</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : subjects.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="textSecondary">
                              No subjects found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        subjects
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((subject, index) => (
                            <TableRow 
                              key={subject.id}
                              sx={{ 
                                '&:hover': { bgcolor: 'grey.50' },
                                animation: `fadeIn 0.6s ease-in-out ${index * 0.1}s both`,
                                '@keyframes fadeIn': {
                                  from: { opacity: 0, transform: 'translateY(20px)' },
                                  to: { opacity: 1, transform: 'translateY(0)' },
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  {subject.code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {subject.name}
                                  </Typography>
                                  {subject.description && (
                                    <Typography variant="caption" color="textSecondary">
                                      {subject.description}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {subject.department || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={`${subject.credits} Credits`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {subject.semester ? `Sem ${subject.semester}` : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {subject.classIds && subject.classIds.length > 0 ? (
                                    subject.classIds.slice(0, 2).map((classId) => {
                                      const classData = classes.find(c => c.id === classId);
                                      return classData ? (
                                        <Chip
                                          key={classId}
                                          label={`${classData.name} (${classData.students.length})`}
                                          size="small"
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      ) : null;
                                    })
                                  ) : (
                                    <Typography variant="caption" color="textSecondary">
                                      No classes assigned
                                    </Typography>
                                  )}
                                  {subject.classIds && subject.classIds.length > 2 && (
                                    <Chip
                                      label={`+${subject.classIds.length - 2} more`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={subject.isActive ? 'Active' : 'Inactive'}
                                  color={getStatusColor(subject.isActive)}
                                  size="small"
                                  sx={{ fontWeight: 500 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Edit Subject">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditSubject(subject)}
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={subject.isActive ? 'Deactivate' : 'Activate'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleSubjectStatus(subject)}
                                      sx={{ color: subject.isActive ? 'warning.main' : 'success.main' }}
                                    >
                                      {subject.isActive ? <VisibilityOffIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Subject">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteSubject(subject)}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={subjects.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Paper>
        </Slide>

        {/* Floating Action Button for Mobile */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="add subject"
            onClick={handleCreateSubject}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
            }}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Subject Form Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallScreen}
          PaperProps={{
            sx: { 
              borderRadius: isSmallScreen ? 0 : 3,
              margin: isSmallScreen ? 0 : 2
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingSubject ? 'Edit Subject' : 'Add New Subject'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Subject Name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    error={!!validationErrors.name}
                    helperText={validationErrors.name}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Subject Code"
                    value={formData.code}
                    onChange={handleInputChange('code')}
                    error={!!validationErrors.code}
                    helperText={validationErrors.code}
                  />
                </Box>
              </Stack>
              
              <Box>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  multiline
                  rows={3}
                />
              </Box>
              
              <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Credits"
                    type="number"
                    value={formData.credits}
                    onChange={handleInputChange('credits')}
                    error={!!validationErrors.credits}
                    helperText={validationErrors.credits}
                    inputProps={{ min: 1, max: 10 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Department"
                    value={formData.department}
                    onChange={handleInputChange('department')}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Semester"
                    type="number"
                    value={formData.semester}
                    onChange={handleInputChange('semester')}
                    error={!!validationErrors.semester}
                    helperText={validationErrors.semester}
                    inputProps={{ min: 1, max: 8 }}
                  />
                </Box>
              </Stack>
              
              <Box>
                <Autocomplete
                  multiple
                  options={classes}
                  getOptionLabel={(option) => option.name}
                  value={classes.filter(c => formData.classIds.includes(c.id))}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, classIds: newValue.map(c => c.id) }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign to Classes"
                      placeholder="Select classes for this subject"
                      error={!!validationErrors.classIds}
                      helperText={validationErrors.classIds || "Students from selected classes will be automatically enrolled"}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={`${option.name} (${option.students.length} students)`}
                        {...getTagProps({ index })}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))
                  }
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setOpenDialog(false)} 
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
              variant={isMobile ? 'outlined' : 'text'}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {editingSubject ? 'Update Subject' : 'Create Subject'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)}
          fullScreen={isSmallScreen}
          PaperProps={{
            sx: { 
              borderRadius: isSmallScreen ? 0 : 3,
              margin: isSmallScreen ? 0 : 2
            }
          }}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the subject "{subjectToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              sx={{ textTransform: 'none', width: isMobile ? '100%' : 'auto' }}
              variant={isMobile ? 'outlined' : 'text'}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete} 
              color="error" 
              variant="contained"
              sx={{ 
                textTransform: 'none', 
                borderRadius: 2,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
} 